export interface ModalOptions {
  title: string
  size?: 'small' | 'medium' | 'large' | 'full'
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
  showCloseButton?: boolean
  onClose?: () => void
  className?: string
}

export class Modal {
  private modalElement: HTMLElement | null = null
  private options: ModalOptions
  private isOpen: boolean = false
  private keydownHandler: (e: KeyboardEvent) => void

  constructor(options: ModalOptions) {
    this.options = {
      size: 'medium',
      closeOnBackdrop: true,
      closeOnEscape: true,
      showCloseButton: true,
      ...options
    }

    this.keydownHandler = this.handleKeydown.bind(this)
  }

  public open(content: string | HTMLElement): void {
    if (this.isOpen) return

    this.createModalElement()
    this.setContent(content)
    this.addToDOM()
    this.setupEventListeners()
    this.isOpen = true

    // Prevent body scroll
    document.body.style.overflow = 'hidden'
    
    // Smooth fade in
    requestAnimationFrame(() => {
      this.modalElement?.classList.add('modal-open')
    })
  }

  public close(): void {
    if (!this.isOpen) return

    this.modalElement?.classList.remove('modal-open')
    
    // Wait for animation to complete
    setTimeout(() => {
      this.removeFromDOM()
      this.cleanup()
      this.isOpen = false
      
      // Restore body scroll
      document.body.style.overflow = ''
      
      // Call onClose callback
      this.options.onClose?.()
    }, 300)
  }

  public updateContent(content: string | HTMLElement): void {
    if (!this.isOpen) return
    this.setContent(content)
  }

  private createModalElement(): void {
    const sizeClass = this.getSizeClass()
    const customClass = this.options.className || ''
    
    this.modalElement = document.createElement('div')
    this.modalElement.className = `modal-backdrop ${customClass}`
    this.modalElement.innerHTML = `
      <div class="modal-container ${sizeClass}">
        <div class="modal-header">
          <h2 class="modal-title">${this.options.title}</h2>
          ${this.options.showCloseButton ? '<button class="modal-close-btn" aria-label="Close modal">&times;</button>' : ''}
        </div>
        <div class="modal-body">
          <!-- Content will be inserted here -->
        </div>
      </div>
    `
  }

  private getSizeClass(): string {
    switch (this.options.size) {
      case 'small': return 'modal-sm'
      case 'medium': return 'modal-md'
      case 'large': return 'modal-lg'
      case 'full': return 'modal-full'
      default: return 'modal-md'
    }
  }

  private setContent(content: string | HTMLElement): void {
    const bodyElement = this.modalElement?.querySelector('.modal-body')
    if (!bodyElement) return

    if (typeof content === 'string') {
      bodyElement.innerHTML = content
    } else {
      bodyElement.innerHTML = ''
      bodyElement.appendChild(content)
    }
  }

  private addToDOM(): void {
    if (this.modalElement) {
      document.body.appendChild(this.modalElement)
    }
  }

  private removeFromDOM(): void {
    if (this.modalElement) {
      document.body.removeChild(this.modalElement)
    }
  }

  private setupEventListeners(): void {
    if (!this.modalElement) return

    // Close button
    const closeBtn = this.modalElement.querySelector('.modal-close-btn')
    closeBtn?.addEventListener('click', () => this.close())

    // Backdrop click
    if (this.options.closeOnBackdrop) {
      this.modalElement.addEventListener('click', (e) => {
        if (e.target === this.modalElement) {
          this.close()
        }
      })
    }

    // Escape key
    if (this.options.closeOnEscape) {
      document.addEventListener('keydown', this.keydownHandler)
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.close()
    }
  }

  private cleanup(): void {
    if (this.options.closeOnEscape) {
      document.removeEventListener('keydown', this.keydownHandler)
    }
  }

  public isModalOpen(): boolean {
    return this.isOpen
  }
} 